'use strict';

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import PlanToPdfButton from '../publishing/PlanToPdfButton.react';
import FavoriteButton from '@components/Widgets/FavoriteButton.react';
import SharePopup from '@components/Widgets/SharePopup.react';
import Popup from '@components/Widgets/Popup.react';
import './PrimaryActions.scss';

const PrimaryActions = ({
    plan,
    permissions,
    onUpdatePlan,
    showPdfPrint,
    showAddToPlanner,
    startSendToPatient,
    onSchedulePlan,
    canSchedule,
    patient,
    user,
    isPro,
    isMobile,
    // Board/Favoriting props
    onFavorite,
    onBeforeUnfavorite,
    boarded,
    saving,
    // Save plan props
    saveState,
    savePlan,
    justSaved,
    // Toolbar actions props (for mobile "More" menu)
    clearPlan,
    showGroceries,
    addDay,
    showProfile,
    maxOffset,
    // Duplicate props
    duplicatePlan,
    isDuplicating
}) => {
    // Extract repeated conditionals to named constants
    const isVirtualPlan = !plan?.links?.self;
    const canModifyPlan = isVirtualPlan ? permissions.canCreate : permissions.canWrite;
    const showDuplicateButton = plan?.links?.self; // Only show if plan is saved

    // State for mobile title editing
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const titleInputRef = useRef(null);

    // Auto-focus input when switching to edit mode
    useEffect(() => {
        if (isEditingTitle && titleInputRef.current) {
            titleInputRef.current.focus();
        }
    }, [isEditingTitle]);

    const handleTitleClick = () => {
        if (isMobile && !isEditingTitle && (permissions.canWrite || permissions.canCreate)) {
            setIsEditingTitle(true);
        }
    };

    const handleTitleBlur = () => {
        if (isMobile) {
            setIsEditingTitle(false);
        }
    };

    const [isSavingForShare, setIsSavingForShare] = useState(false);
    
    const handleShareClick = async () => {
        // Save before sharing when the plan has unsaved edits OR is still virtual (never
        // persisted to the DB). A freshly generated virtual plan is not "dirty" (no edits),
        // but its UUID is local-only — sharing it as-is produces a /menus/{uuid} link that
        // 404s for anyone else. Saving converts it to a real plan with a shareable UUID.
        const isVirtual = !plan?.links?.self;
        if ((saveState?.isDirty || isVirtual) && savePlan) {
            setIsSavingForShare(true);
            try {
                await savePlan();
            } catch (error) {
                //Plan failed to save
            } finally {
                setIsSavingForShare(false);
            }
        }
    };

    const canEditTitle = permissions.canWrite || permissions.canCreate;
    const showTitleInput = !isMobile || isEditingTitle;

    return (
        <div className="primary-actions">
            <div className="primary-actions-left">
                <h1 className="plan-title-wrapper">
                    {showTitleInput ? (
                        <input
                            ref={titleInputRef}
                            type="text"
                            className="plan-title"
                            value={plan.title || ''}
                            onChange={(ev) => onUpdatePlan({ title: ev.target.value })}
                            onBlur={handleTitleBlur}
                            placeholder="Enter meal plan name"
                            disabled={!canEditTitle}
                        />
                    ) : (
                        <div
                            className="plan-title-display"
                            onClick={handleTitleClick}
                            data-editable={canEditTitle}
                        >
                            {plan.title || 'Enter meal plan name'}
                        </div>
                    )}
                </h1>
            </div>

            {/* Action Buttons */}
            <div className="primary-actions-right">
                {/* Header buttons - stay in header on all screen sizes */}
                <div className="primary-actions-header-buttons">
                    {/* 1. Export Button */}
                    {plan.uuid && (
                        <SharePopup
                            plan={plan}
                            buttonText=""
                            dropdownBtnClass="dropdown-btn el-text-btn"
                            positionClassName="el-popup-top-right"
                            popupText={isSavingForShare ? "Saving plan..." : "Share this meal plan by copying the link below:"}
                            onClick={handleShareClick}
                            loading={isSavingForShare}
                        />
                    )}

                    {/* 2. Favorite Icon */}
                    <FavoriteButton
                        plan={plan}
                        onFavorite={onFavorite}
                        onBeforeUnfavorite={onBeforeUnfavorite}
                        boarded={boarded}
                        icon={'feather feather-heart'}
                        iconBoarded={'feather feather-heart-on'}
                    />

                    {/* 3. PDF Button */}
                    {showPdfPrint && plan.links && plan.links.self && (
                        <PlanToPdfButton
                            plan={plan}
                            classes="plan-to-pdf-btn"
                            button={<><span>PRINT OR SAVE PDF</span></>}
                            patient={patient}
                        />
                    )}

                    {/* More Menu - Mobile only */}
                    <Popup
                        button={<i className="feather feather-more-vertical" />}
                        dropdownBtnClass="dropdown-btn el-text-btn more-menu-btn"
                        positionClassName="el-popup-top-right more-menu-popup"
                    >
                        <div>
                            {/* 1. Print or Save PDF */}
                            {showPdfPrint && plan.links && plan.links.self && (
                                <PlanToPdfButton
                                    plan={plan}
                                    classes="more-menu-option plan-to-pdf-more-menu"
                                    button={<><i className="feather feather-printer" /><span>Print or Save PDF</span></>}
                                    patient={patient}
                                />
                            )}

                            {/* 2. View Plan Settings */}
                            {user && (isPro || user.role === 'admin') && (
                                <button className="more-menu-option" onClick={showProfile}>
                                    <i className="feather feather-eye" />
                                    <span>View Plan Settings</span>
                                </button>
                            )}

                            {/* 3. Preview Grocery List */}
                            {user && (
                                <button className="more-menu-option" onClick={showGroceries}>
                                    <i className="feather feather-shopping-cart" />
                                    <span>Preview Grocery List</span>
                                </button>
                            )}

                            {/* 4. Add a Day */}
                            <button className="more-menu-option" onClick={addDay}>
                                <i className="feather feather-plus" />
                                <span>Add a Day</span>
                            </button>

                            {/* 5. Clear Meals */}
                            {canModifyPlan && (
                                <button className="more-menu-option" onClick={clearPlan}>
                                    <i className="feather feather-trash-2" />
                                    <span>Clear Meals</span>
                                </button>
                            )}

                            {/* 6. Duplicate Meal Plan */}
                            {showDuplicateButton && (
                                <button className="more-menu-option" onClick={duplicatePlan} disabled={isDuplicating}>
                                    <i className="feather feather-copy" />
                                    <span>{isDuplicating ? 'Duplicating...' : 'Duplicate Meal Plan'}</span>
                                </button>
                            )}
                        </div>
                    </Popup>
                </div>

                {/* Footer buttons - positioned as footer on mobile via CSS */}
                <div className="primary-actions-footer-buttons">
                    {/* 4. Recommend Button */}
                    {(user?.dietitian && isPro) && (
                        <button
                            className="el-medium-btn el-raspberry-btn recommend-btn"
                            data-testid="plan-editor-recommend-btn"
                            onClick={startSendToPatient}
                        >
                            {isMobile ? 'Recommend' : (patient ? `Recommend to ${patient.first_name}` : 'Recommend to ...')}
                        </button>
                    )}

                    {/* 5. Save Meal Plan Button */}
                    {canModifyPlan && saveState?.shouldShowSaveButton() && (
                        <div className="save-button-group">
                            <button
                                className="el-medium-btn el-green-btn"
                                onClick={savePlan}
                                disabled={saveState.isSaving || !saveState.shouldEnableSaveButton()}
                                aria-label={saveState.isSaving ? 'Saving meal plan' : saveState.isDirty ? 'Save meal plan' : saveState.lastSaveTime ? 'Meal plan saved' : 'Save meal plan'}
                            >
                                {saveState.isSaving ? 'Saving...' : saveState.isDirty ? 'SAVE MEAL PLAN' : saveState.lastSaveTime ? 'Saved' : 'Save'}
                            </button>

                            {/* Save status - shows below save button */}
                            {saveState.shouldShowSaveStatus() && (
                                <p className="save-status" data-error={saveState.lastError != null} role="status" aria-live="polite" aria-atomic="true">
                                    {saveState.getSaveStatusMessage()}
                                </p>
                            )}

                            {/* Success message - appears below save button */}
                            <p className="last-saved" data-visible={justSaved} role="status" aria-live="polite" aria-atomic="true">
                                Meal plan saved!
                            </p>
                        </div>
                    )}

                    {/* 6. Schedule Button (non-pro only) */}
                    {!isPro && (
                        <button
                            className="el-medium-btn el-raspberry-btn"
                            onClick={onSchedulePlan}
                            disabled={!canSchedule}
                        >
                            Schedule This Plan
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

PrimaryActions.propTypes = {
    plan: PropTypes.object.isRequired,
    permissions: PropTypes.object.isRequired,
    onUpdatePlan: PropTypes.func.isRequired,
    showPdfPrint: PropTypes.bool,
    onSchedulePlan: PropTypes.func,
    showAddToPlanner: PropTypes.bool,
    startSendToPatient: PropTypes.func,
    patient: PropTypes.object,
    user: PropTypes.object.isRequired,
    isPro: PropTypes.bool,
    isMobile: PropTypes.bool,
    canSchedule: PropTypes.bool,
    // Board/Favoriting props
    onFavorite: PropTypes.func,
    onBeforeUnfavorite: PropTypes.func,
    boarded: PropTypes.bool,
    saving: PropTypes.bool,
    // Save plan props
    saveState: PropTypes.shape({
        isDirty: PropTypes.bool.isRequired,
        isSaving: PropTypes.bool.isRequired,
        lastSaveTime: PropTypes.instanceOf(Date),
        lastError: PropTypes.object,
        getSaveStatusMessage: PropTypes.func.isRequired,
        shouldShowSaveStatus: PropTypes.func.isRequired,
        shouldShowSaveButton: PropTypes.func.isRequired,
    }),
    savePlan: PropTypes.func,
    justSaved: PropTypes.bool,
    // Toolbar actions props (for mobile "More" menu)
    clearPlan: PropTypes.func,
    showGroceries: PropTypes.func,
    addDay: PropTypes.func,
    showProfile: PropTypes.func,
    maxOffset: PropTypes.number,
    // Duplicate props
    duplicatePlan: PropTypes.func,
    isDuplicating: PropTypes.bool,
};

PrimaryActions.defaultProps = {
    showPdfPrint: false,
    showAddToPlanner: false,
};

export default PrimaryActions;
